/**
 * PlayViewerModal - Full-screen modal for viewing and animating plays.
 * Provides slideshow navigation between plays in a playbook.
 * Uses React Query for data fetching with proper caching and prefetching.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Pencil, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { Button } from '../ui/Button'
import { Loading } from '../ui/Loading'
import { AnimationProvider } from '../../contexts/AnimationContext'
import { AnimationCanvas } from './AnimationCanvas'
import { AnimationControls } from './AnimationControls'
import { usePlayAnimation } from '@/hooks/UsePlayAnimation.ts'
import {
	type PlayContent,
	fetchPlayContent,
	playKeys,
	usePlayContent,
} from '@/api/queries/PlayQueries'
import './play-viewer-modal.css'

type PlayMetadata = {
	id: string
	name: string
}

type PlayViewerModalProps = {
	isOpen: boolean
	onClose: () => void
	playbookId: string
	initialPlayId: string
	plays: PlayMetadata[]
	canEdit?: boolean
}

function LoadingState() {
	return <Loading text="Loading play..." size="lg" />
}

function ErrorState({ message }: { message: string }) {
	return (
		<div className="play-viewer-error">
			<p className="play-viewer-error-title">Failed to load play</p>
			<p className="play-viewer-error-message">{message}</p>
		</div>
	)
}

function PlayViewerContent({
							   playbookId,
							   playContent,
							   currentMetadata,
							   currentIndex,
							   totalPlays,
							   isLoading,
							   error,
							   onClose,
							   canEdit = false,
						   }: {
	playbookId: string
	playContent: PlayContent | null | undefined
	currentMetadata: PlayMetadata
	currentIndex: number
	totalPlays: number
	isLoading: boolean
	error: string | null
	onClose: () => void
	canEdit?: boolean
}) {
	const navigate = useNavigate()

	// Adapt React Query PlayContent to hook's expected shape
	const animationPlayContent = useMemo(() => {
		if (!playContent) return null
		return {
			id: playContent.id,
			name: playContent.name,
			players: playContent.players,
			drawings: playContent.drawings,
		}
	}, [playContent])

	usePlayAnimation({ playContent: animationPlayContent })

	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === 'Escape') {
				onClose()
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [onClose])

	const handleEdit = useCallback(() => {
		navigate(`/playbooks/${playbookId}/play/${currentMetadata.id}`)
	}, [navigate, playbookId, currentMetadata.id])

	const displayName =
		playContent?.name || currentMetadata.name || 'Untitled Play'

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.2 }}
			className="play-viewer-modal"
		>
			{/* Header */}
			<div className="play-viewer-header">
				<div className="play-viewer-header-left">
					<Button
						variant="ghost"
						size="icon"
						onClick={onClose}
						aria-label="Close (Esc)"
						className="play-viewer-close-button"
					>
						<X className="size-6" />
					</Button>
					<div className="play-viewer-title-group">
						<h2 className="play-viewer-title">
							{displayName}
						</h2>
						<span className="play-viewer-counter">
							{currentIndex + 1} of {totalPlays}
						</span>
					</div>
				</div>

				{canEdit && (
					<Button
						variant="outline"
						onClick={handleEdit}
						className="play-viewer-edit-button"
					>
						<Pencil className="size-4" />
						Edit
					</Button>
				)}
			</div>

			{/* Canvas area */}
			{isLoading ? (
				<LoadingState />
			) : error ? (
				<ErrorState message={error} />
			) : (
				<div className="play-viewer-canvas">
					<AnimationCanvas
						drawings={playContent?.drawings ?? []}
						players={playContent?.players ?? []}
					/>
				</div>
			)}

			{/* Controls */}
			<div className="play-viewer-controls-footer">
				<AnimationControls />
			</div>
		</motion.div>
	)
}

/**
 * Prefetches adjacent plays (prev/next) for smoother navigation transitions.
 * Uses React Query's prefetchQuery to warm the cache without blocking render.
 */
function useAdjacentPlayPrefetch(
	plays: PlayMetadata[],
	currentIndex: number,
	enabled: boolean,
) {
	const queryClient = useQueryClient()

	useEffect(() => {
		if (!enabled || plays.length === 0) return

		const adjacentIds: string[] = []

		// Prefetch previous play
		if (currentIndex > 0) {
			const prevPlay = plays[currentIndex - 1]
			if (prevPlay) adjacentIds.push(prevPlay.id)
		}

		// Prefetch next play
		if (currentIndex < plays.length - 1) {
			const nextPlay = plays[currentIndex + 1]
			if (nextPlay) adjacentIds.push(nextPlay.id)
		}

		for (const playId of adjacentIds) {
			queryClient.prefetchQuery({
				queryKey: playKeys.content(playId),
				queryFn: () => fetchPlayContent(playId),
				staleTime: 5 * 60 * 1000,
			})
		}
	}, [currentIndex, plays, enabled, queryClient])
}

export function PlayViewerModal({
									isOpen,
									onClose,
									playbookId,
									initialPlayId,
									plays,
									canEdit = false,
								}: PlayViewerModalProps) {
	const [currentIndex, setCurrentIndex] = useState(() => {
		const index = plays.findIndex((p) => p.id === initialPlayId)
		return index >= 0 ? index : 0
	})

	const currentMetadata = plays[currentIndex]
	const currentPlayId = currentMetadata?.id ?? null

	// Use React Query for play content (proper caching, no cache-busting timestamps)
	const { data: playContent, isLoading, error } = usePlayContent(
		currentPlayId,
		{ enabled: isOpen && currentPlayId !== null },
	)

	// Prefetch adjacent plays for smoother prev/next navigation
	useAdjacentPlayPrefetch(plays, currentIndex, isOpen)

	useEffect(() => {
		const index = plays.findIndex((p) => p.id === initialPlayId)
		if (index >= 0) {
			setCurrentIndex(index)
		}
	}, [initialPlayId, plays])

	const errorMessage = error instanceof Error ? error.message : error ? String(error) : null

	return (
		<AnimatePresence>
			{isOpen && currentMetadata && (
				<AnimationProvider>
					<PlayViewerContent
						playbookId={playbookId}
						playContent={playContent}
						currentMetadata={currentMetadata}
						currentIndex={currentIndex}
						totalPlays={plays.length}
						isLoading={isLoading}
						error={errorMessage}
						onClose={onClose}
						canEdit={canEdit}
					/>
				</AnimationProvider>
			)}
		</AnimatePresence>
	)
}
